import { useState, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Trash2, Plus, Minus, MapPin, Wallet, CalendarDays } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { formatRupiah } from '@/lib/utils'
import { useCart } from '@/context/CartContext'
import { useAuth } from '@/hooks/useAuth'
import { useRekening } from '@/hooks/useRekening'
import { FloatingInput, FloatingTextarea, FloatingDateInput } from '@/components/FloatingInput'
import { useJadwalBlokir, generateSlots, isBlocked, isPast } from '@/hooks/useJadwalBlokir'
import { useOrg } from '@/context/OrgContext'

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function Pesanan() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isRental, orgId } = useOrg()
  const { items, orderData, setOrderData, removeItem, updateQty, getSubtotal, clearCart, isLoading } = useCart()
  const { jadwal } = useJadwalBlokir()
  const { rekening } = useRekening()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [metodeBayar, setMetodeBayar] = useState<'tunai' | 'transfer'>(orderData?.metode_bayar || 'tunai')
  const [selectedRekeningId, setSelectedRekeningId] = useState(orderData?.rekening_id || '')
  const [form, setForm] = useState({
    nama: orderData?.nama_pemesan || '',
    alamat: orderData?.alamat || '',
    noHp: orderData?.no_hp || '',
    tanggal: orderData?.tanggal_kirim || '',
    jam: orderData?.jam_kirim || '',
  })
  const [touched, setTouched] = useState({
    nama: false,
    alamat: false,
    tanggal: false,
    jam: false,
  })

  if (isLoading) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center text-gray-400">
        <p>Loading...</p>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center text-gray-400">
        <p>{isRental ? 'Belum ada item sewa.' : 'Pesanan kosong.'} Silakan tambahkan item.</p>
        <button onClick={() => navigate('/')} className="mt-4 text-sm text-blue-600 hover:underline">
          Kembali ke beranda
        </button>
      </div>
    )
  }

  const subtotal = getSubtotal()
  const today = new Date().toISOString().split('T')[0]
  const slots = generateSlots(6, 22)

  // For rental: use earliest tanggal_mulai from items as tanggal_kirim
  const tanggalKirimRental = items
    .map(i => i.tanggal_mulai)
    .filter(Boolean)
    .sort()[0] ?? today

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    let value = e.target.value
    if (e.target.name === 'noHp') value = value.replace(/[^\d]/g, '')
    setForm(f => ({ ...f, [e.target.name]: value }))
  }

  function handleBlur(field: 'nama' | 'alamat' | 'tanggal' | 'jam') {
    setTouched(t => ({ ...t, [field]: true }))
  }

  const efektifMetode: 'tunai' | 'transfer' = isRental ? 'transfer' : metodeBayar

  const isFormValid = isRental
    ? form.nama.trim() !== '' && selectedRekeningId !== ''
    : form.nama.trim() !== '' &&
      form.alamat.trim() !== '' &&
      form.tanggal !== '' &&
      form.jam !== ''

  async function handleCheckout() {
    if (isSubmitting || !isFormValid) return
    if (efektifMetode === 'transfer' && !selectedRekeningId) {
      alert('Pilih rekening bank untuk transfer')
      return
    }
    setIsSubmitting(true)

    try {
      const orderId = crypto.randomUUID()

      const { error: orderError } = await supabase.from('orders').insert({
        id: orderId,
        nama_pemesan: form.nama.trim(),
        alamat: isRental ? null : form.alamat.trim(),
        no_hp: form.noHp.trim() || null,
        tanggal_kirim: isRental ? tanggalKirimRental : form.tanggal,
        jam_kirim: isRental ? null : form.jam,
        metode_bayar: efektifMetode,
        rekening_id: efektifMetode === 'transfer' ? selectedRekeningId : null,
        pelanggan_id: user?.id ?? null,
        total: subtotal,
        organization_id: orgId,
      })

      if (orderError) throw new Error(`Gagal membuat order: ${orderError.message}`)

      const invalidItem = items.find(item => !item.nama_produk)
      if (invalidItem) throw new Error('Data cart tidak valid. Silakan hapus item dan tambahkan ulang.')

      const orderItems = items.map(item => ({
        order_id: orderId,
        produk_id: item.produk_id,
        nama_produk: item.nama_produk,
        harga_satuan: item.harga,
        qty: item.qty,
        subtotal: isRental
          ? item.harga * item.qty * (item.jumlah_hari || 1)
          : item.harga * item.qty,
        jumlah_hari: isRental ? (item.jumlah_hari ?? null) : null,
      }))

      const { error: itemsError } = await supabase.from('order_items').insert(orderItems)
      if (itemsError) throw new Error(`Gagal menambah item: ${itemsError.message}`)

      clearCart()
      navigate(`/status?order_id=${orderId}`)
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Gagal membuat pesanan. Silakan coba lagi.')
      console.error('Checkout error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const btnDisabled = isSubmitting || items.length === 0 || !isFormValid
  const btnClass = `w-full py-3.5 rounded-xl font-semibold text-sm transition-colors ${
    btnDisabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
  }`

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-28 md:pb-12 flex flex-col md:block">
      <div className="flex items-center mb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ChevronLeft size={16} />
          Kembali
        </button>
        <h1 className="text-base font-bold text-gray-900 absolute left-1/2 -translate-x-1/2">
          {isRental ? 'Review Sewa' : 'Review Pesanan'}
        </h1>
      </div>

      <div className="md:grid md:grid-cols-2 md:gap-8 md:items-start">

        {/* LEFT COLUMN */}
        <div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-4 mb-6">
            <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-4">
              <MapPin size={16} className="text-blue-500" />
              {isRental ? 'Data Penyewa' : 'Form Pemesanan'}
            </h2>

            <div className="space-y-4">
              <FloatingInput
                label="Nama Pemesan *"
                name="nama"
                value={form.nama}
                onChange={handleChange}
                onBlur={() => handleBlur('nama')}
                error={touched.nama && !form.nama.trim() ? 'Nama wajib diisi' : undefined}
              />

              {!isRental && (
                <FloatingTextarea
                  label="Alamat *"
                  name="alamat"
                  value={form.alamat}
                  onChange={handleChange}
                  onBlur={() => handleBlur('alamat')}
                  rows={3}
                  error={touched.alamat && !form.alamat.trim() ? 'Alamat wajib diisi' : undefined}
                />
              )}

              <FloatingInput
                label="No. HP (opsional)"
                name="noHp"
                type="tel"
                value={form.noHp}
                onChange={handleChange}
              />

              {!isRental && (
                <>
                  <FloatingDateInput
                    label="Tanggal Kirim *"
                    value={form.tanggal}
                    min={today}
                    onChange={val => {
                      setForm(f => ({ ...f, tanggal: val, jam: '' }))
                      setTouched(t => ({ ...t, tanggal: true }))
                    }}
                    error={touched.tanggal && !form.tanggal ? 'Tanggal pengiriman wajib diisi' : undefined}
                  />

                  {form.tanggal && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Pilih Jam *</p>
                      <div className="flex flex-wrap gap-2">
                        {slots.map(hour => {
                          const dayOfWeek = new Date(form.tanggal).getDay()
                          const blocked = isBlocked(hour, dayOfWeek, jadwal)
                          const past = isPast(hour, form.tanggal)
                          const disabled = blocked || past
                          const label = `${String(hour).padStart(2, '0')}:00`
                          const selected = form.jam === label
                          return (
                            <button
                              key={hour}
                              type="button"
                              disabled={disabled}
                              onClick={() => {
                                setForm(f => ({ ...f, jam: label }))
                                setTouched(t => ({ ...t, jam: true }))
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                disabled
                                  ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                                  : selected
                                  ? 'border-blue-600 bg-blue-600 text-white'
                                  : 'border-gray-200 text-gray-700 hover:border-blue-400 hover:text-blue-600'
                              }`}
                            >
                              {label}
                            </button>
                          )
                        })}
                      </div>
                      {touched.jam && !form.jam && (
                        <p className="text-xs text-red-500 mt-1 ml-1">Jam pengiriman wajib dipilih</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {isRental && (
                <div className="bg-blue-50 rounded-xl p-3 text-sm">
                  <p className="text-xs text-gray-500 font-medium mb-1">Info Pengambilan</p>
                  <p className="text-gray-700">Ambil di tempat · Bayar via transfer</p>
                  <p className="text-xs text-gray-400 mt-1">Admin akan mengkonfirmasi setelah pembayaran masuk</p>
                </div>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-4 mt-6">
            <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-4">
              <Wallet size={16} className="text-blue-500" />
              Metode Pembayaran
            </h2>

            {isRental ? (
              <div className="space-y-3">
                <div className="py-2.5 px-3 rounded-xl border border-blue-600 bg-blue-50 text-sm font-medium text-blue-600">
                  Transfer Bank
                </div>
                <p className="text-xs text-gray-400">Penyewaan wajib bayar via transfer sebelum barang diambil.</p>
                <label className="block text-sm font-medium text-gray-900">Pilih Rekening Bank *</label>
                <select
                  value={selectedRekeningId}
                  onChange={e => setSelectedRekeningId(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Pilih Rekening --</option>
                  {rekening.map(rek => (
                    <option key={rek.id} value={rek.id}>
                      {rek.bank} · {rek.nomor} ({rek.atas_nama})
                    </option>
                  ))}
                </select>
                {selectedRekeningId && rekening.find(r => r.id === selectedRekeningId) && (
                  <div className="bg-blue-50 rounded-xl p-3 text-sm">
                    <p className="text-gray-600 font-semibold">{rekening.find(r => r.id === selectedRekeningId)?.atas_nama}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {rekening.find(r => r.id === selectedRekeningId)?.bank} · {rekening.find(r => r.id === selectedRekeningId)?.nomor}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setMetodeBayar('tunai')}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                      metodeBayar === 'tunai' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    Tunai
                  </button>
                  <button
                    type="button"
                    onClick={() => setMetodeBayar('transfer')}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                      metodeBayar === 'transfer' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    Transfer Bank
                  </button>
                </div>
                {metodeBayar === 'tunai' && (
                  <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-3">Siapkan uang tunai saat belanja datang.</p>
                )}
                {metodeBayar === 'transfer' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-900">Pilih Rekening Bank *</label>
                    <select
                      value={selectedRekeningId}
                      onChange={e => setSelectedRekeningId(e.target.value)}
                      className="w-full px-3 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="">-- Pilih Rekening --</option>
                      {rekening.map(rek => (
                        <option key={rek.id} value={rek.id}>
                          {rek.bank} · {rek.nomor} ({rek.atas_nama})
                        </option>
                      ))}
                    </select>
                    {selectedRekeningId && rekening.find(r => r.id === selectedRekeningId) && (
                      <div className="bg-blue-50 rounded-xl p-3 text-sm">
                        <p className="text-gray-600 font-semibold">{rekening.find(r => r.id === selectedRekeningId)?.atas_nama}</p>
                        <p className="text-gray-500 text-xs mt-1">
                          {rekening.find(r => r.id === selectedRekeningId)?.bank} · {rekening.find(r => r.id === selectedRekeningId)?.nomor}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {/* END LEFT */}

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-6 md:gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-4">
            <h2 className="text-sm font-bold text-gray-900 mb-4">
              {isRental ? 'Daftar Item Sewa' : 'Daftar Item'}
            </h2>
            <div className="space-y-3">
              {items.map(item => (
                <div key={item.produk_id} className="flex gap-4 p-3 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center text-2xl shrink-0 overflow-hidden">
                    {item.gambar_url ? (
                      <img src={item.gambar_url} alt={item.nama_produk} className="w-full h-full object-cover" />
                    ) : (
                      '🎒'
                    )}
                  </div>

                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{item.nama_produk}</p>
                      {isRental ? (
                        <p className="text-blue-600 font-semibold text-xs">
                          {formatRupiah(item.harga)} / hari
                        </p>
                      ) : item.harga_max ? (
                        <p className="text-blue-600 font-semibold text-xs">
                          {formatRupiah(item.harga)} – {formatRupiah(item.harga_max)}
                          <span className="text-gray-400 font-normal ml-1">*estimasi</span>
                        </p>
                      ) : (
                        <p className="text-blue-600 font-semibold text-xs">
                          {formatRupiah(item.harga)} / {item.satuan}
                        </p>
                      )}

                      {/* Rental info */}
                      {isRental && item.tanggal_mulai && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                          <CalendarDays size={11} />
                          <span>{fmtDate(item.tanggal_mulai)}</span>
                          <span>→</span>
                          <span className="text-gray-600">{addDays(item.tanggal_mulai, item.jumlah_hari ?? 1)}</span>
                          <span className="ml-1 bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">
                            {item.jumlah_hari}h
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={() => updateQty(item.produk_id, item.qty - 1)}
                        className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{item.qty}</span>
                      <button
                        onClick={() => updateQty(item.produk_id, item.qty + 1)}
                        className="w-6 h-6 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeItem(item.produk_id)}
                        className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-sm">
                      {formatRupiah(item.harga * item.qty * (item.jumlah_hari || 1))}
                    </p>
                    {isRental && item.jumlah_hari && item.jumlah_hari > 1 && (
                      <p className="text-xs text-gray-400">{item.jumlah_hari} hari</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 rounded-2xl p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total{items.some(i => i.harga_max) ? ' (estimasi)' : ''}</span>
              <span className="font-bold text-blue-600 text-lg">{formatRupiah(subtotal)}</span>
            </div>
            {items.some(i => i.harga_max) && (
              <p className="text-xs text-gray-500 mt-1">*Harga akhir beberapa layanan ditentukan saat pengerjaan</p>
            )}
          </div>

          <button
            onClick={() => {
              setOrderData({
                nama_pemesan: form.nama.trim(),
                alamat: form.alamat.trim(),
                no_hp: form.noHp.trim() || undefined,
                tanggal_kirim: form.tanggal,
                jam_kirim: form.jam,
                metode_bayar: efektifMetode,
                rekening_id: efektifMetode === 'transfer' ? selectedRekeningId : undefined,
              })
              navigate('/item')
            }}
            className="w-full py-3 rounded-xl font-semibold text-sm border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors mb-3"
          >
            + Tambah Item Lain
          </button>

          <button onClick={handleCheckout} disabled={btnDisabled} className={btnClass}>
            {isSubmitting ? 'Memproses...' : isRental ? 'Konfirmasi Sewa' : 'Checkout'}
          </button>
        </div>
        {/* END RIGHT */}

      </div>
    </div>
  )
}
